<script lang="ts">
    import CookieSvg from './CookieSvg.svelte';
    import translations from '../translations';
    import Accordion from './Accordion/Accordion.svelte';
    import IntegrationToggle from './Accordion/IntegrationToggle.svelte';
    import { integrationsToDisplayName, integrations } from '$lib/global/tracking/integrations';
    import { getIntegrationsByCategory } from '../helper';
    import AdditionalDescription from './Accordion/AdditionalDescription.svelte';
    import { mediaCookiesAccepted } from '$lib/Stores/stores';
    import { base } from '$app/paths';

    // Types
    import type { Language } from '$lib/types/Language';
    import type { IntegrationCategory } from '$lib/components/ConsentOverlay/types';

    export let language: Language = 'de';
    export let termsLink = '';
    export let legalLink = '';
    export let acceptCookies = (integrations: Record<string, boolean>) => {
        console.warn('No acceptCookies function provided', integrations);
    };
    export let disableCookiePoliciesLink = false;
    export let integrationsToShow: string[] = [];
    export let storedIntegrations: Record<string, boolean> = {};

    let selectedIntegrations: Record<string, boolean> = {
        Perspective: true,
    };
    let showMore = false;

    $: storedIntegrations,
        integrationsToShow.forEach((integration) => {
            selectedIntegrations[integration] =
                storedIntegrations[integration] === undefined
                    ? false
                    : storedIntegrations[integration];
        });

    const marketingIntegrations = getIntegrationsByCategory(integrationsToShow, 'marketing');
    const analyticsIntegrations = getIntegrationsByCategory(integrationsToShow, 'analytics');
    const showMediaCategory = integrationsToShow.includes(integrationsToDisplayName['media']);
    const showSelectionButton = integrationsToShow.length > 0;
    const showRequiredToggle =
        marketingIntegrations.length > 0 || marketingIntegrations.length > 0 || showMediaCategory;

    const handleCategoryToggle = (category: string, value: boolean) => {
        const updatedIntegrations = Object.assign({}, selectedIntegrations);
        const integrationsToUpdate = getIntegrationsByCategory(
            integrationsToShow,
            category as IntegrationCategory,
        );

        integrationsToUpdate.forEach((integration) => {
            updatedIntegrations[integration] = value;
        });

        selectedIntegrations = updatedIntegrations;
    };

    const handleIntegrationToggle = (name: string, value: boolean) => {
        selectedIntegrations = {
            ...selectedIntegrations,
            [name]: value,
        };
    };

    const handleAcceptAll = () => {
        const integrationsToLoad: Record<string, boolean> = {};
        integrationsToShow.forEach((integration) => {
            integrationsToLoad[integration] = true;
        });

        acceptCookies(integrationsToLoad);

        if (integrationsToLoad.Media) {
            $mediaCookiesAccepted = true;
        }
    };

    const handleAcceptSelection = () => {
        acceptCookies({
            ...selectedIntegrations,
            Perspective: true,
        });

        if (selectedIntegrations.Media) {
            $mediaCookiesAccepted = true;
        }
    };

    const handleReject = () => {
        acceptCookies({ Perspective: true });
    };

    const handleLearnMore = () => {
        showMore = true;
    };

    $: marketingCategoryEnabled = !marketingIntegrations.some(
        (integration) => selectedIntegrations[integration] === false,
    );
    $: analyticsCategoryEnabled = !analyticsIntegrations.some(
        (integration) => selectedIntegrations[integration] === false,
    );
    $: mediaCategoryEnabled = selectedIntegrations[integrationsToDisplayName[integrations.media]];
</script>

<div
    class="w-[calc(100% - 16px)] fixed bottom-2 right-2 left-2 z-[999] max-h-[496px] overflow-auto rounded-lg bg-gray-50 font-sans shadow-lg sm:relative sm:left-auto sm:right-auto sm:w-[360px]"
>
    <div class="p-6 text-gray-700">
        <CookieSvg />
        <h3 class="mb-3 font-semibold text-gray-800">{translations[language].headline}</h3>
        {#if !showMore}
            <p class="text-sm leading-relaxed">
                {translations[language].introText}
                <button class="underline hover:text-gray-900" on:click={handleLearnMore}>
                    {translations[language].learnMore}
                </button>
            </p>
        {:else}
            <p class="text-sm leading-relaxed">
                {translations[language].longConsent}
            </p>
            <h3 class="mt-4 text-sm font-medium text-gray-800">
                {translations[language].headlineProcessing}
            </h3>
            <p class="text-sm leading-relaxed">
                {translations[language].longProcessing}
            </p>
        {/if}
        <div class="mt-3 text-sm">
            {#if legalLink}
                <a
                    href={legalLink}
                    class="pr-2 text-gray-700 underline hover:text-gray-900"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {translations[language].imprint}
                </a>
            {/if}
            {#if termsLink}
                <a
                    href={termsLink}
                    class="pr-2 text-gray-700 underline hover:text-gray-900"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {translations[language].privacy}
                </a>
            {/if}
            {#if !disableCookiePoliciesLink}
                <a
                    href={`${base}/consent-policy`}
                    class="pr-2 text-gray-700 underline hover:text-gray-900"
                >
                    {translations[language].cookieLink}
                </a>
            {/if}
        </div>

        <div class="mb-4 text-sm leading-relaxed text-gray-700">
            <Accordion
                title={translations[language].cookies.required.title}
                disabledToggle={showRequiredToggle}
                active
            >
                <div class="mt-4 pl-[22px] text-gray-700">
                    {translations[language].cookies.required.description}
                    <AdditionalDescription {language} />
                </div>
            </Accordion>
            {#if marketingIntegrations.length > 0}
                <Accordion
                    title={translations[language].cookies.marketing.title}
                    onToggle={handleCategoryToggle}
                    categoryId="marketing"
                    active={marketingCategoryEnabled}
                >
                    <div class="mt-4 pl-[22px] text-gray-700">
                        {translations[language].cookies.marketing.description}
                        <AdditionalDescription {language} />
                        {#each marketingIntegrations as integration}
                            <IntegrationToggle
                                text={integration}
                                id={integration}
                                onToggle={handleIntegrationToggle}
                                active={selectedIntegrations[integration]}
                            />
                        {/each}
                    </div>
                </Accordion>
            {/if}
            {#if analyticsIntegrations.length > 0}
                <Accordion
                    title={translations[language].cookies.analytics.title}
                    onToggle={handleCategoryToggle}
                    categoryId="analytics"
                    active={analyticsCategoryEnabled}
                >
                    <div class="mt-4 pl-[22px] text-gray-700">
                        {translations[language].cookies.marketing.description}
                        <AdditionalDescription {language} />
                        {#each analyticsIntegrations as integration}
                            <IntegrationToggle
                                text={integration}
                                id={integration}
                                onToggle={handleIntegrationToggle}
                                active={selectedIntegrations[integration]}
                            />
                        {/each}
                    </div>
                </Accordion>
            {/if}
            {#if showMediaCategory}
                <Accordion
                    title={translations[language].cookies.media.title}
                    onToggle={handleCategoryToggle}
                    categoryId={'media'}
                    active={mediaCategoryEnabled}
                >
                    <div class="mt-4 pl-[22px] text-gray-700">
                        {translations[language].cookies.media.description}
                        <AdditionalDescription {language} />
                    </div>
                </Accordion>
            {/if}
        </div>

        <button
            class="h-12 w-full flex-1 items-center justify-center rounded-lg bg-blue-500 text-sm font-medium text-white hover:bg-blue-600"
            on:click={handleAcceptAll}
        >
            {showSelectionButton
                ? translations[language].acceptAll
                : translations[language].acceptNecessary}
        </button>
        <div class="flex space-x-3">
            {#if showSelectionButton}
                <button
                    class="mt-3 flex h-12 w-full flex-1 items-center justify-center rounded-lg bg-blue-100 text-sm font-medium text-blue-500 hover:text-blue-600"
                    on:click={handleAcceptSelection}
                >
                    {translations[language].acceptSelection}
                </button>
            {/if}
            <button
                class="mt-3 flex h-12 w-full flex-1 items-center justify-center rounded-lg bg-blue-100 text-sm font-medium text-blue-500 hover:text-blue-600"
                class:ml-2={showSelectionButton}
                class:flex-0={showSelectionButton}
                on:click={handleReject}
            >
                {translations[language].rejectAll}
            </button>
        </div>
    </div>
</div>
